<!-- eslint-disable vue/no-v-html -->
<!-- The text under a video (Bespoke.vue), 2026-10-14: the talk's description, then each speaker's
     bio with a run-in "BIO" heading, then "See more videos by …" for a speaker with other videos
     (/Videos/First_Last, the speaker's gallery). The bios come after the description in the Vimeo
     text, one paragraph each, starting with the speaker's name (fullstack
     scripts/update/videos/descriptions.js), so a paragraph starting with a speaker's name is a bio.
     A paragraph found in that year's people sheet shows the sheet's HTML, with its links (as the
     People highlights; sheetHtml.js); our own sheet, so v-html. A speaker with no bio in the Vimeo
     text (a video uploaded by hand) gets the People sheets' bio. The speaker's name that starts a
     bio links to their highlight (the speaker dialog, as on /Videos/Unlock; Seán, 2026-10-30). -->
<template>
  <div class="videoDescription">
    <p
      v-for="(paragraph, index) in paragraphs"
      :key="index"
      class="description"
      :class="{isBio: paragraph.isBio}">
      <span v-if="paragraph.isBio" class="runIn">Bio.</span
      ><a
        v-if="paragraph.speakerName"
        class="speakerName"
        :href="getPeoplePath(paragraph.speakerName)"
        @click.prevent="openSpeaker(paragraph.speakerName, null, year)"
      >{{ paragraph.lead }}</a
      ><span v-if="paragraph.html" v-html="paragraph.html" /><template v-else>{{
        paragraph.text
      }}</template>
    </p>
    <p v-for="speaker in speakersWithMore" :key="speaker.path" class="more">
      <router-link :to="speaker.path">See more videos by {{ speaker.name }}</router-link>
    </p>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import useSpeakerDialog from "@/composables/useSpeakerDialog";
import useSpeakerIndex, {getSpeakerKey} from "@/composables/useSpeakerIndex";
import {getPlainText, getSheetHtmlByText, getSpeakerBio} from "@/components/Video/sheetHtml";

// description: the Vimeo text; videoName: "A & B: Title" (the speakers come from it); year: the
// video's (its people sheet)
const props = defineProps({
  description: {type: String, default: ""},
  videoName: {type: String, default: ""},
  year: {type: [String, Number], default: ""}
});

// "A & B: Title" → ["A", "B"] ([] for a video not named after people)
function getSpeakerNames(videoName) {
  const [speakerPart, ...titleParts] = String(videoName || "").split(": ");
  return titleParts.length ? speakerPart.split(" & ").map((name) => name.trim()) : [];
}

// as getSpeakerKey, and any apostrophe the same: "O’Donoghue" (a bio) = "O'Donoghue" (the name)
function getNameKey(text) {
  return getSpeakerKey(text).replace(/[’‘`]/g, "'");
}

function getIsBio(text, speakerKeys) {
  const key = getNameKey(text);
  return speakerKeys.some((speakerKey) => key.startsWith(speakerKey));
}

// the fallback address of a speaker's highlight (the dialog opens instead)
function getPeoplePath(name) {
  return `/${props.year}/People#${name.replaceAll(" ", "_")}`;
}

// the sheet's HTML without the link it may put on the bio's opening name (the speaker's site:
// the highlight keeps it, as its 🔗)
function getWithoutLeadLink(html) {
  return html?.replace(/^<a\b[^>]*>([^<]*)<\/a>/i, "$1");
}

// where the first `length` visible characters of the HTML end (an entity, "&aacute;", is one);
// -1 when a tag comes first
function getHtmlIndex(html, length) {
  let index = 0;
  for (let count = 0; count < length; count += 1) {
    if (html[index] === "<") return -1;
    const entity = html.slice(index).match(/^&[#\w]+;/);
    index += entity ? entity[0].length : 1;
  }
  return index;
}

// a bio's opening name, as the bio writes it ("Seán O’Donoghue"), split off for its link; left
// whole when the sheet's HTML doesn't start with it
function getWithNameLink(bio, names) {
  const html = getWithoutLeadLink(bio.html);
  if (!bio.isBio) return bio;
  const speakerName = names.find((name) => getNameKey(bio.text).startsWith(getNameKey(name)));
  const lead = bio.text.slice(0, speakerName?.length);
  if (!speakerName || getNameKey(lead) !== getNameKey(speakerName)) return bio;
  const rest = {text: bio.text.slice(lead.length), html};
  if (!html) return {...bio, ...rest, speakerName, lead};
  const htmlIndex = getHtmlIndex(html, lead.length);
  const htmlLead = htmlIndex < 0 ? "" : getPlainText(html.slice(0, htmlIndex));
  if (getNameKey(htmlLead) !== getNameKey(lead)) return bio;
  rest.html = html.slice(htmlIndex);
  return {...bio, ...rest, speakerName, lead};
}

const {getSpeaker} = useSpeakerIndex();
const {openSpeaker} = useSpeakerDialog();
const speakerNames = computed(() => getSpeakerNames(props.videoName));
const paragraphs = ref([]);
async function fetchParagraphs() {
  const speakerKeys = speakerNames.value.map(getNameKey);
  const htmlByText = await getSheetHtmlByText(props.year, speakerNames.value.map(getSpeakerKey));
  // a paragraph's single line breaks are the sheet's hard wraps (2026-11-05: a bio starting
  // "Marta\nSzachniuk" wasn't seen as a bio, so the sheets' bio came again below it)
  const paragraphTexts = props.description.split(/\n\s*\n/);
  const texts = paragraphTexts.map((text) => text.replace(/\s+/g, " ").trim()).filter(Boolean);
  const fromVimeo = texts.map((text) => {
    return {text, html: htmlByText[getPlainText(text)], isBio: getIsBio(text, speakerKeys)};
  });
  const all = [...fromVimeo, ...await getMissingBios(fromVimeo)];
  paragraphs.value = all.map((paragraph) => getWithNameLink(paragraph, speakerNames.value));
}
watch(() => [props.videoName, props.description, props.year], fetchParagraphs, {immediate: true});

// a speaker with no bio in the Vimeo text (a video uploaded by hand): the People sheets' bio
async function getMissingBios(fromVimeo) {
  const withBio = fromVimeo.filter((paragraph) => paragraph.isBio).map((p) => getNameKey(p.text));
  const missing = speakerNames.value.filter((name) => {
    return !withBio.some((key) => key.startsWith(getNameKey(name)));
  });
  const bios = await Promise.all(missing.map((name) => {
    return getSpeakerBio(props.year, getSpeakerKey(name));
  }));
  return bios.filter(Boolean).map((html) => ({text: getPlainText(html), html, isBio: true}));
}
// more than this video in the speaker index; a one-word name has no /Videos/First_Last route
const speakersWithMore = computed(() => {
  const speakers = speakerNames.value.map(getSpeaker).filter((speaker) => speaker?.name);
  const withMore = speakers.filter((speaker) => speaker.videos?.length > 1);
  return withMore.filter((speaker) => speaker.name.includes(" ")).map((speaker) => ({
    name: speaker.name,
    path: `/Videos/${speaker.name.replaceAll(" ", "_")}`
  }));
});
</script>

<style scoped>
.description {
  white-space: pre-wrap;
}
/* a bio: a little more space above it, to set it off from the talk's description */
.description.isBio {
  margin-top: 1.25em;
}
/* a run-in heading, "BIO.": bold capitals, a little larger than the text, on the bio's first
   line; the strongest text colour, white in dark mode (2026-10-14) */
.darkMode .runIn {
  color: #fff;
}
.runIn {
  margin-right: 0.45em;
  font-size: 1.1em;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
/* the speaker's name opens their highlight here: a control in running text, so the brand's
   green, not a link's blue (DESIGN.md § 5; Seán, 2026-10-30), as TimeZoneInline.vue */
.speakerName {
  color: color-mix(in srgb, var(--brand-dark-color) 80%, black);
}
.darkMode .speakerName {
  color: color-mix(in srgb, var(--brand-color) 55%, var(--text-color));
}
.more {
  margin-top: 0.5rem;
}
.narrow .description {
  font-size: clamp(0.75rem, 3.33dvw, 1rem);
}
</style>
